/**
 * documentactions.js
 * Created by calhosh on 9/9/2020.
 */

import * as types from '../actiontypes';
import Env from '../../util/env';
import {documentCategoryNames, documentTypes, entityTypes, promiseAreas, serviceURL} from '../../util/uiconstants';
import {API_RESOURCES, createApiReadAction} from '../../util/api';

export const getAllDocuments = (url, taskId, resolve = false) => {
	return {
		type: types.GET_ALL_DOCUMENTS,
		meta: {
			api: {
				url,
				method: 'GET'
			},
			taskId: taskId
		},
		resolve
	};
};

export const getAllAresDocuments = (
	engagementId,
	taskIdList,
	documentTypeIdList,
	documentCategoryName = documentCategoryNames.evidence,
	payload,
	pageSize = undefined,
	includeRelatedEntities,
	selectedEntityId
) => {
	let queryParameters = {
		documentCategoryName: documentCategoryName,
		documentTypeIdList: documentTypeIdList,
		filterTaskId: taskIdList,
		includeApprovals: true,
		includeRelatedTasks: true,
		includeReviewNotes: true
	};

	if (includeRelatedEntities) {
		queryParameters.includeRelatedEntities = includeRelatedEntities;

		if (selectedEntityId) {
			queryParameters.filterMultiEntityDocuments = selectedEntityId;
		}
	}

	if (pageSize !== undefined) {
		queryParameters.pageSize = pageSize;
	}

	return createApiReadAction(
		types.GET_ALL_DOCUMENTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.DOCUMENTS]: undefined
		},
		queryParameters,
		{isAresDocuments: true, resolve: true, ...payload},
		undefined,
		promiseAreas.FIT_BREADCRUMB
	);
};

export function getDocumentsByTaskIdList(
	engagementId,
	documentCategoryName = documentCategoryNames.evidence,
	taskIdList,
	queryString,
	payload,
	pageSize = undefined
) {
	let strTaskIdList;
	if (taskIdList) {
		if (Array.isArray(taskIdList)) {
			strTaskIdList = taskIdList?.length > 0 ? taskIdList.join(',') : undefined;
		} else {
			strTaskIdList = taskIdList;
		}
	}

	let queryParameters = {
		documentCategoryName: documentCategoryName,
		filterTaskId: strTaskIdList,
		includeApprovals: true,
		includeRelatedTasks: true,
		pageSize: -1,
		...queryString
	};

	if (pageSize !== undefined) {
		queryParameters.pageSize = pageSize;
	}

	return createApiReadAction(
		types.MARK_COMPLETE_GET_DOCUMENTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.DOCUMENTS]: undefined
		},
		queryParameters,
		{resolve: true, ...payload}
	);
}

export const getHandOffEvidenceDocuments = (engagementId, taskId) => {
	return createApiReadAction(
		types.GET_ALL_DOCUMENTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.DOCUMENTS]: undefined
		},
		{
			filteredEntityType: entityTypes.Task,
			documentCategoryName: documentCategoryNames.evidence,
			filteredEntityIdGuid: taskId,
			skipRefreshRenderFilters: true,
			pageSize: -1,
			includeReviewNotes: false,
			includeApprovals: true,
			includeFindings: false,
			includeClientApprovals: true,
			includeRelatedTasks: false
		},
		{taskId, isHandOffEvidence: true}
	);
};

/**
 * Fetches documents for a GL Account with evidence, approvals, and review notes
 * Used for the Account Leadsheet 294 references tooltip
 */
export const getGLAccountDocuments = (engagementId, accountId) => {
	return createApiReadAction(
		types.GET_ALL_DOCUMENTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.DOCUMENTS]: undefined
		},
		{
			filteredEntityType: 'GLAccount',
			documentCategoryName: documentCategoryNames.evidence,
			filteredEntityIdInt: accountId,
			includeRelatedTasks: true,
			includeRelatedEntities: true,
			includeApprovals: true,
			includeReviewNotes: true,
			pageSize: -1
		},
		{resolve: true, isGLAccountDocuments: true}
	);
};

/**
 * Fetches a single document by id
 * Calls api/engagements/{engagementId}/documents/{id}
 */
export const getDocumentById = (engagementId, documentId) => {
	return createApiReadAction(
		types.GET_SINGLE_DOCUMENT_BY_ID,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.DOCUMENTS]: documentId
		},
		{
			documentCategoryName: documentCategoryNames.evidence,
			includeApprovals: true,
			includeReviewNotes: true
		},
		{resolve: true}
	);
};

// Makes a call to api/engagements/{engagementId}/documents/{id} with PATCH http verb
export function updateDocument(engagementId, documentId, data, resolvedversionNumber, currentVersionNumber, isHistoryResolve, entityUId, viewKey) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}`;

	if (resolvedversionNumber && resolvedversionNumber > 0) {
		url = url + '?resolvedVersionNumber=' + resolvedversionNumber + '&currentVersionNumber=' + currentVersionNumber + '&isHistoryResolve=' + isHistoryResolve;
	}

	return {
		type: types.UPDATE_DOCUMENT,
		viewKey,
		documentId,
		entityUId,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: data
			}
		}
	};
}

export function unlinkDocument(engagementId, documentId, entityUId, viewKey) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents`;

	return {
		type: types.UNLINK_SHARED_DOCUMENT,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: [
					{
						path: '/UnlinkEvidence', //UnlinkResponse
						value: documentId,
						op: 'remove'
					}
				]
			}
		},
		entityUId,
		viewKey,
		documentId
	};
}

export function unlinkLinkedResponse(engagementId, documentId) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents`;

	return {
		type: types.PATCH_DOCUMENT,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: [
					{
						path: '/UnlinkEvidence', //UnlinkResponse
						value: documentId,
						op: 'remove'
					}
				]
			}
		}
	};
}

export function deleteDocumentById(
	engagementId,
	documentId,
	entityUId,
	viewKey,
	action = 'delete',
	parentName,
	instructionId = null,
	sectionId,
	customErrorMessage,
	type = types.DELETE_DOCUMENT_BY_ID
) {
	const url = getDocumentUrl(engagementId, documentId, undefined, action);

	return {
		type: type,
		viewKey,
		documentId,
		entityUId,
		customErrorMessage,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			},
			tabname: parentName,
			sectionId: sectionId,
			instructionId
		}
	};
}

function getDocumentUrl(engagementId, documentId, expand, action) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}`;

	if (expand) {
		url += `?Expand=${expand.toLowerCase()}`;
	}
	if (action === 'HardDelete') {
		url += '/HardDelete';
	}

	return url;
}

export function setDocumentId(documentId) {
	return {
		type: types.SET_DOCUMENT_ID,
		documentId: documentId?.toLowerCase()
	};
}

export function setIsMFE(isMFE = false) {
	return {
		type: types.SET_IS_MFE,
		isMFE: isMFE
	};
}

export function updateDocumentMetadata(engagementId, documentId, data, payload) {
	const docUrl = `${Env.getDocServiceUrl('documents.json')}/${documentId}`;

	return {
		type: types.UPDATE_DOCUMENT_METADATA,
		documentId: documentId,
		meta: {
			api: {
				url: docUrl,
				method: 'PATCH',
				headers: {
					engagementid: engagementId,
					'patch-metadata': true
				},
				data: data
			}
		},
		payload
	};
}
